import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Col, Form, Input, Row, Space, Spin } from "antd";
import { has } from "lodash-es";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useIntl } from "react-intl";

import { AgentUpdated } from "@/events";
import { useResetForm } from "@/hooks";
import { useAgentCreateSchema, TAgentCreateSchema } from "@/schemas";
import { AgentsStore, useGlobalStore } from "@/stores";
import ErrorsFormatter from "@/ui/_common_/errors-formatter";
import Password from "@/ui/_common_/password";
import { generatePassword } from "@/utilities";
import yup from "@/yup";

type TProps = {
  agentsStore: AgentsStore;
};

function AgentCreateShortForm({ agentsStore }: TProps) {
  const agentCreateSchema = useAgentCreateSchema();
  const { agentCreateStore } = agentsStore;
  const { eventBusService, routerService } = useGlobalStore();
  const schema = useMemo(() => {
    if (agentCreateStore.parentCurrency === null) {
      return agentCreateSchema;
    }
    return agentCreateSchema.concat(
      yup.object({
        currency: yup.string().equals([agentCreateStore.parentCurrency]),
      }),
    );
  }, [agentCreateSchema, agentCreateStore.parentCurrency]);

  const resolver = yupResolver(schema);
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
  } = useForm<TAgentCreateSchema>({
    defaultValues: {
      currency: agentCreateStore.parentCurrency ?? undefined,
      isBalanceUnlimited: !agentCreateStore.isBalanceUnlimitedDisabled,
      canHaveChildren: true,
    },
    resolver,
  });

  const values = watch();

  useResetForm(agentCreateStore.createQuery, reset);

  useEffect(() => {
    setValue("currency", agentCreateStore.parentCurrency ?? undefined);
  }, [setValue, agentCreateStore.parentCurrency]);

  useEffect(() => {
    const listener = (event: AgentUpdated) => {
      const searchParams = new URLSearchParams(routerService.location.search);
      searchParams.set("sq", String(event.payload.agentId));

      routerService.push("/shops");
      routerService.push({
        pathname: "/agents",
        search: searchParams.toString(),
      });
    };

    eventBusService.subscribe(AgentUpdated, listener);

    return () => {
      eventBusService.unsubscribe(AgentUpdated, listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setValue("name", values.login);
  }, [getValues, setValue, values.login]);

  useEffect(() => {
    agentCreateStore.initializeParentAgentSelection();

    return () => agentCreateStore.dispose();
  }, [agentCreateStore]);

  const intl = useIntl();

  return (
    <Spin spinning={agentCreateStore.createQuery.isPending}>
      <Space direction="vertical">
        <ErrorsFormatter queries={[agentCreateStore.createQuery]} />
        <Form
          labelCol={{ span: 8 }}
          labelAlign="left"
          wrapperCol={{ span: 16 }}
          layout="horizontal"
          autoComplete="off"
          onFinish={handleSubmit(agentCreateStore.handleSubmit)}
        >
          <Form.Item
            label={intl.formatMessage({ defaultMessage: "Admin username" })}
            validateStatus={has(errors, "login") ? "error" : ""}
            help={errors.login?.message}
          >
            <Controller
              control={control}
              name="login"
              defaultValue=""
              render={({ field }) => (
                <>
                  <input type="text" style={{ display: "none" }} />
                  <Input
                    {...field}
                    placeholder={intl.formatMessage({
                      defaultMessage: "Enter username",
                    })}
                    autoComplete="new-password"
                  />
                </>
              )}
            />
          </Form.Item>
          <Form.Item
            label={intl.formatMessage({ defaultMessage: "Admin password" })}
            validateStatus={has(errors, "password") ? "error" : ""}
            help={errors.password?.message}
          >
            <Controller
              control={control}
              name="password"
              defaultValue=""
              render={({ field }) => (
                <>
                  <input type="password" style={{ display: "none" }} />
                  <Password
                    {...field}
                    placeholder={intl.formatMessage({
                      defaultMessage: "Enter password",
                    })}
                    autoComplete="new-password"
                    onCopy={() => {
                      const password = getValues().password ?? "";
                      if (password) {
                        navigator.clipboard.writeText(password);
                      }
                    }}
                    onGenerate={() => setValue("password", generatePassword())}
                  />
                </>
              )}
            />
          </Form.Item>
          <Row justify="end" gutter={12}>
            <Col>
              <Button type="primary" htmlType="submit">
                {intl.formatMessage({ defaultMessage: "Create" })}
              </Button>
            </Col>
          </Row>
        </Form>
      </Space>
    </Spin>
  );
}

export default observer(AgentCreateShortForm);
